import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { FileBarChart2, Download, Calendar, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const AdminReports = () => {
  const [modo, setModo] = useState("general"); // "general" | "periodo"
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    if (modo === "periodo") {
      if (!fechaInicio || !fechaFin) {
        toast.error("Debes seleccionar fecha de inicio y fecha fin.");
        return;
      }
      if (fechaInicio > fechaFin) {
        toast.error("La fecha de inicio no puede ser mayor a la fecha fin.");
        return;
      }
    }

    setLoading(true);
    try {
      const params = {};
      if (modo === "periodo") {
        params.fecha_inicio = fechaInicio;
        params.fecha_fin    = fechaFin;
      }

      const response = await axios.get(`${API}/reports/pdf/users-status`, {
        params,
        responseType: "blob",
      });

      const url  = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href  = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      link.setAttribute("download", `reporte_estado_usuarios_${today}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Reporte descargado correctamente.");
    } catch (err) {
      console.error(err);
      toast.error("Error al generar el reporte. Intente nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Encabezado */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
          <FileBarChart2 className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Reporte de Usuarios</h1>
          <p className="text-sm text-slate-500">Estado de capacitaciones de todos los usuarios</p>
        </div>
      </div>

      {/* Selector de modo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        {/* Opción: Total general */}
        <button
          onClick={() => setModo("general")}
          className={`rounded-xl border-2 p-4 text-left transition-all ${
            modo === "general"
              ? "border-primary bg-primary/5"
              : "border-slate-200 bg-white hover:border-slate-300"
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              modo === "general" ? "bg-primary/10" : "bg-slate-100"
            }`}>
              <Users className={`w-4 h-4 ${modo === "general" ? "text-primary" : "text-slate-400"}`} />
            </div>
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
              modo === "general" ? "border-primary" : "border-slate-300"
            }`}>
              {modo === "general" && <div className="w-2 h-2 rounded-full bg-primary" />}
            </div>
          </div>
          <p className={`font-semibold text-sm ${modo === "general" ? "text-primary" : "text-slate-700"}`}>
            Reporte general
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Todos los usuarios con todos sus cursos matriculados
          </p>
        </button>

        {/* Opción: Filtrar por periodo */}
        <button
          onClick={() => setModo("periodo")}
          className={`rounded-xl border-2 p-4 text-left transition-all ${
            modo === "periodo"
              ? "border-primary bg-primary/5"
              : "border-slate-200 bg-white hover:border-slate-300"
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              modo === "periodo" ? "bg-primary/10" : "bg-slate-100"
            }`}>
              <Calendar className={`w-4 h-4 ${modo === "periodo" ? "text-primary" : "text-slate-400"}`} />
            </div>
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
              modo === "periodo" ? "border-primary" : "border-slate-300"
            }`}>
              {modo === "periodo" && <div className="w-2 h-2 rounded-full bg-primary" />}
            </div>
          </div>
          <p className={`font-semibold text-sm ${modo === "periodo" ? "text-primary" : "text-slate-700"}`}>
            Filtrar por fecha
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Cursos activos en el mes o periodo seleccionado
          </p>
        </button>
      </div>

      {/* Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">

        {/* Selector de fechas — solo si modo periodo */}
        {modo === "periodo" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Fecha inicio
              </label>
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Fecha fin
              </label>
              <input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          </div>
        )}

        {/* Info del reporte */}
        <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-sm text-slate-600">
          <p className="font-medium text-slate-700">El reporte incluye:</p>
          <ul className="space-y-1 list-disc list-inside">
            <li>Listado completo de usuarios con su estado actual</li>
            <li>Clasificación: <span className="font-medium text-green-700">Activo</span>, <span className="font-medium text-orange-600">Pendiente</span> o <span className="font-medium text-red-600">Sin acceso</span></li>
            <li>Último acceso a la plataforma</li>
            <li>Capacitaciones realizadas vs. matriculadas</li>
            <li>Resumen ejecutivo con porcentajes</li>
          </ul>
        </div>

        <Button
          onClick={handleDownload}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2"
          size="lg"
        >
          {loading ? (
            <>
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Generando reporte...
            </>
          ) : (
            <>
              <Download className="w-4 h-4" />
              Descargar Reporte PDF
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AdminReports;
